import styled, {css} from 'styled-components'
import React from 'react'
import {Tooltip} from '../BaseComponents'
import {TABLE_SCROLL_EDGE_ATTRIBUTES} from './useTableScrollEdges'

/**
 * The width at or below which a `TableHeaderRow` hides (unless `keepOnNarrow`) and
 * `MobileTableWrapper` restacks its rows, as a media query. Exported so a page can adapt its own
 * controls at the same width (e.g. an icon-only Columns trigger through `useMediaQuery`).
 */
export const TABLE_NARROW_QUERY = '(max-width: 968px)'

/*
 * ── Opt-in layout (STFFLG-4179) ──
 *
 * Every prop below is opt-in: left unset, the table renders exactly as before, and none of them
 * reaches the DOM as an attribute (about a hundred tables use these pieces).
 */

/** `compact` swaps `--row-data-padding` for `--row-data-padding-compact` on every cell. */
export type TableDensity = 'default' | 'compact'

/** Where a sticky cell sticks: the inline start (left) or end (right) of the scroll container. */
export type TableSticky = 'start' | 'end'

export interface TableLayoutProps {
  /**
   * `overflow: clip` instead of `hidden`. Needed for sticky cells: an `overflow: hidden` table is
   * a scroll container of its own, so its sticky cells would stick to it and scroll away with it.
   */
  clip?: boolean
  /** `compact`: `--row-data-padding-compact` on every header and body cell. */
  density?: TableDensity
  /**
   * Body cells paint their row's `--row-bg` (else `--bg-card`), and a hovered body row sets it to
   * `--bg-subtle`, so a row state fills every cell, sticky ones included. A row state (a highlight,
   * a tint) sets `--row-bg` on its `tr` instead of painting its `td`s; any class rule outranks the
   * hover (which matches with the specificity of `tbody > tr`).
   *
   * - `TableRow selected` sets `--row-bg` (`--brand-50`), so a selected row fills every cell, and
   *   stays `--brand-50` when hovered.
   * - The hover applies to every body row, a `TableRow plain` without `hover` included, and uses
   *   `--bg-subtle`, not `plain hover`'s `--bg-hover`.
   * - The cell rule (`table > tbody > tr > td`) outranks a `styled(TableCell)` `background`. A
   *   per-cell tint sets `--row-bg` on the cell itself instead: its own declaration beats the one it
   *   inherits from the row.
   */
  rowFill?: boolean
}

export interface TableStickyProps {
  /**
   * Sticks the cell to the scroll container's start or end edge: `position: sticky`,
   * `z-index: var(--z-elevated)` (over the neighbouring cells' `position: relative` tooltip
   * wrappers, which would otherwise paint over it and take its clicks) and an opaque fill, its
   * row's `--row-bg` (a header cell: the header fill). The table needs `clip`. Under a container
   * kept by `useTableScrollEdges` it shows `--shadow-sticky-edge` on its inner side while content
   * is scrolled under it.
   */
  sticky?: TableSticky
  /** The sticky inset (a CSS length, e.g. the width of a sticky column before it); default 0. */
  stickyOffset?: string
}

const TABLE_LAYOUT_PROPS: readonly string[] = ['clip', 'density', 'rowFill']
const STICKY_PROPS: readonly string[] = ['sticky', 'stickyOffset']

/**
 * A sticky cell's rules. `fill` is the cell's own surface when its row sets no `--row-bg` (a header
 * cell's is `--neutral-100` even under `TableHeader plain`). The edge shadow is clipped to the side
 * content scrolls on (a start cell's inline end, an end cell's inline start); a start column stuck
 * beside another (expand + #ID) hides the shadow of the one before it under itself, so only the
 * last one shows. The placement is logical (`inset-inline-*`), but the shadow's clip is physical,
 * for a left-to-right table: in a right-to-left one it would be clipped to the wrong side.
 */
const stickyCell =
  (fill: string) =>
  ({sticky, stickyOffset}: TableStickyProps) =>
    sticky &&
    css`
      position: sticky;
      ${sticky === 'start' ? 'inset-inline-start' : 'inset-inline-end'}: ${stickyOffset ?? '0'};
      z-index: var(--z-elevated);
      background: var(--row-bg, ${fill});

      [${TABLE_SCROLL_EDGE_ATTRIBUTES[sticky]}='false'] & {
        box-shadow: var(--shadow-sticky-edge);
        clip-path: ${sticky === 'start' ? 'inset(0 -1rem 0 0)' : 'inset(0 0 0 -1rem)'};
      }
    `

// Base Table Structure
const Table = styled.table.withConfig({
  // Layout-only props — don't forward to the DOM <table> (React warns on unknown attrs).
  shouldForwardProp: (prop) => !TABLE_LAYOUT_PROPS.includes(prop),
})<TableLayoutProps>`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background: var(--bg-card);
  overflow: ${({clip}) => (clip ? 'clip' : 'hidden')};
  ${({density}) =>
    density === 'compact' &&
    css`
      --row-data-padding: var(--row-data-padding-compact);
    `}
  ${({rowFill}) =>
    rowFill &&
    css`
      :where(&) > tbody > tr:where(:hover) {
        --row-bg: var(--bg-subtle);
      }

      & > tbody > tr > td {
        background: var(--row-bg, var(--bg-card));
      }
    `}
`

const TableHeader = styled.thead.withConfig({
  shouldForwardProp: (prop) => prop !== 'plain',
})<{plain?: boolean}>`
  /* Gray header fill (design review — supersedes the AG §2.1 divider-only treatment).
     \`plain\` keeps the old no-fill header for the carve-out spreadsheet tables. */
  background: ${(props) => (props.plain ? 'transparent' : 'var(--neutral-100)')};
`

const TableHeaderRow = styled.tr.withConfig({
  shouldForwardProp: (prop) => prop !== 'keepOnNarrow',
})<{
  /**
   * Keep the header row (and its sort controls) at every width. By default it hides at
   * `TABLE_NARROW_QUERY`, for tables that restack into cards there (`MobileTableWrapper`); a table
   * that scrolls sideways instead keeps it.
   */
  keepOnNarrow?: boolean
}>`
  height: var(--row-data, 2.5rem);
  ${({keepOnNarrow}) =>
    !keepOnNarrow &&
    css`
      @media ${TABLE_NARROW_QUERY} {
        display: none;
      }
    `}
`

const TableHeaderCell = styled.th.withConfig({
  // Style-only props — don't forward to the DOM <th> (React warns on unknown attrs).
  shouldForwardProp: (prop) =>
    !['width', 'sortable', 'textAlign', 'spreadsheet', ...STICKY_PROPS].includes(prop),
})<
  {
    width?: string
    sortable?: boolean
    textAlign?: 'left' | 'center' | 'right'
    spreadsheet?: boolean
  } & TableStickyProps
>`
  padding: var(--row-data-padding, var(--space-2) var(--space-3));
  /* G9: breathing room before the first column / after the last so table content
     doesn't hug the card edge (aligns closer to the card header inset). The
     spreadsheet carve-out tables (narrow # column) opt out. */
  ${(props) =>
    !props.spreadsheet &&
    `
    &:first-child { padding-left: var(--space-5); }
    &:last-child { padding-right: var(--space-5); }
  `}
  text-align: ${(props) => props.textAlign ?? 'left'};
  font-size: var(--fs-micro, 0.6875rem);
  font-weight: var(--fw-semibold, 600);
  /* Darker header text (matches the sortable/bill-code tables) — unified across
     all app tables for a consistent, more legible column-header tone. */
  color: var(--neutral-700);
  text-transform: uppercase;
  letter-spacing: var(--ls-wide, 0.05em);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: transparent;
  /* Top + bottom border so the header band is a closed line at the top of the
     table (design review: "there's no line in the up line of the row"). */
  border-top: var(--border-thin, 1px) solid var(--neutral-300);
  border-bottom: var(--border-thin, 1px) solid var(--neutral-300);
  ${(props) =>
    props.spreadsheet &&
    `
    background: var(--neutral-100);
    border-right: var(--border-thin, 1px) solid var(--neutral-300);
    border-left: var(--border-thin, 1px) solid var(--neutral-300);
  `}
  ${(props) => props.width && `width: ${props.width};`}
  ${(props) => props.sortable && `cursor: pointer;`}

  &:hover {
    ${(props) => props.sortable && `color: var(--brand-cta);`}
  }

  .sort-content {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    justify-content: ${(props) => (props.textAlign === 'center' ? 'center' : 'flex-start')};
  }
  /* The header fill (the thead's), unless the row sets --row-bg. */
  ${stickyCell('var(--neutral-100)')}
`

const TableBody = styled.tbody``

const TableRow = styled.tr.withConfig({
  // Style-only props — don't forward to the DOM <tr> (React warns on unknown attrs).
  shouldForwardProp: (prop) => !['hover', 'selected', 'plain'].includes(prop),
})<{hover?: boolean; selected?: boolean; plain?: boolean}>`
  height: var(--row-data, 2.5rem);
  background: ${(props) => (props.selected ? 'var(--brand-50)' : 'var(--bg-card)')};
  /* Under Table rowFill (and in sticky cells) the cells paint --row-bg, so the selection must set it. */
  ${({selected}) =>
    selected &&
    css`
      --row-bg: var(--brand-50);
    `}
  transition: background var(--motion-fast, 150ms) var(--motion-ease);

  /* Row hover is default-on app-wide (design review). \`plain\` reverts to the legacy
     opt-in hover for the carve-out spreadsheet tables. */
  ${(props) =>
    props.plain
      ? props.hover && `&:hover { background: var(--bg-hover); }`
      : `&:hover { background: var(--bg-subtle); }`}
`

const StyledTableCell = styled.td.withConfig({
  // Style-only props — don't forward to the DOM <td> (React warns on unknown attrs).
  shouldForwardProp: (prop) =>
    !['width', 'textAlign', 'showBorder', 'spreadsheet', ...STICKY_PROPS].includes(prop),
})<
  {
    width?: string
    textAlign?: 'left' | 'center' | 'right'
    showBorder?: boolean
    spreadsheet?: boolean
  } & TableStickyProps
>`
  padding: var(--row-data-padding, var(--space-2) var(--space-3));
  /* G9: first/last column breathing room (see header cell). */
  ${(props) =>
    !props.spreadsheet &&
    `
    &:first-child { padding-left: var(--space-5); }
    &:last-child { padding-right: var(--space-5); }
  `}
  text-align: ${(props) => props.textAlign ?? 'left'};
  font-size: var(--fs-body, 0.875rem);
  color: var(--neutral-700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: var(--bg-card);
  border-bottom: var(--border-thin, 1px) solid var(--divider);
  ${(props) => props.width && `width: ${props.width};`}
  ${(props) =>
    props.showBorder && `border-right: var(--border-thin, 1px) solid var(--neutral-400);`}
  ${(props) =>
    props.spreadsheet &&
    `
    border-right: var(--border-thin, 1px) solid var(--neutral-300);
    border-left: var(--border-thin, 1px) solid var(--neutral-300);
    border-top: var(--border-thin, 1px) solid var(--neutral-300);
    border-bottom: var(--border-thin, 1px) solid var(--neutral-300);
  `}
  ${stickyCell('var(--bg-card)')}
`

// Mobile Table Wrapper — based on working ronos-2.0 implementation
const MobileTableWrapper = styled.div`
  .cell-header {
    display: none;
  }

  @media ${TABLE_NARROW_QUERY} {
    .thead {
      display: none !important;
    }
    .table-header {
      display: none;
    }

    .table-row {
      display: block !important;
      height: auto !important;
      border: var(--border-thin, 1px) solid var(--border-subtle);
      border-radius: var(--radius-sm, 8px);
      margin-bottom: var(--space-3);
      padding: var(--space-4);
      background: var(--bg-card) !important;
    }

    .table-cell {
      display: flex !important;
      justify-content: space-between;
      align-items: flex-start;
      padding: var(--space-2) 0;
      border-bottom: var(--border-thin, 1px) solid var(--neutral-100);
      height: auto !important;
      min-height: auto !important;
    }

    .table-cell:last-child {
      border-bottom: none;
    }

    .cell-header {
      display: block;
      font-weight: var(--fw-semibold, 600);
      color: var(--neutral-700);
      font-size: var(--fs-caption, 0.75rem);
      margin-bottom: var(--space-1);
    }
  }
`

/**
 * One line of text cut with "…" when its box is too narrow. It shows no tooltip of its own: wrap it
 * as `<Tooltip content={value} block><EllipsisText>{value}</EllipsisText></Tooltip>` (or use
 * `TableCell ellipsis`), and the DS Tooltip shows the whole value only while it is cut off
 * (STFFLG-4236). Never add a native `title`: it shows over text the eye can already read.
 */
export const EllipsisText = styled('span')`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 100%;
`

export interface TableCellProps
  extends Omit<React.TdHTMLAttributes<HTMLTableCellElement>, 'width'>,
    TableStickyProps {
  width?: string
  textAlign?: 'left' | 'center' | 'right'
  showBorder?: boolean
  spreadsheet?: boolean
  /**
   * Truncate text content with an ellipsis + a hover tooltip showing the full
   * value, which shows only while the text is actually cut off (read on hover,
   * so a resized column counts; STFFLG-4236). Use on TEXT cells only (the table
   * is `table-layout: fixed`, so the column width constrains it). Do NOT use on
   * cells whose content is a Badge / StatusPill / button — those self-truncate
   * and shouldn't be clipped, and wrapping them would put an ellipsis on the
   * whole control.
   */
  ellipsis?: boolean
}

/**
 * TableCell — the styled `<td>` plus an opt-in `ellipsis` mode. When `ellipsis`
 * is set, text content truncates within the (fixed) column width and, once it
 * ends in "…", shows the full value on hover. Reuses the shared `EllipsisText` +
 * `Tooltip block` so the truncation chain isn't broken by an intermediate wrapper.
 */
const TableCell: React.FC<TableCellProps> = ({ellipsis, children, ...rest}) => {
  if (!ellipsis) return <StyledTableCell {...rest}>{children}</StyledTableCell>
  // Tooltip shows the full value on hover while the text is cut off (it repeats
  // the cell's text, so the DS gates it on truncation). When children isn't a
  // plain string we can't derive text, so content is '' and Tooltip renders no
  // bubble (guarded).
  const tooltipContent = typeof children === 'string' ? children : ''
  return (
    <StyledTableCell {...rest}>
      <Tooltip content={tooltipContent} block>
        <EllipsisText>{children}</EllipsisText>
      </Tooltip>
    </StyledTableCell>
  )
}

/** One `<col>` of a `TableColGroup`. */
export interface TableColumnWidth {
  /** The column's key (its id). */
  id: string
  /** A CSS width (rem or a token). None: the column shares what the fixed columns leave. */
  width?: string
}

const TableCol = styled.col<{$width?: string}>`
  ${({$width}) =>
    $width &&
    css`
      width: ${$width};
    `}
`

/**
 * The table's column widths as a `<colgroup>`, one `<col>` per column in order (STFFLG-4179). Under
 * the DS Table's `table-layout: fixed` a `<col>` width sizes its column whatever the rows hold, and
 * it holds when the header row is hidden, which widths on header cells do not. Leave one column
 * without a width to flex, and give the table a `min-width` of the fixed widths plus that column's
 * minimum, so a narrower container scrolls instead of squeezing it.
 */
const TableColGroup = ({
  columns,
  'data-testid': testId,
}: {
  columns: readonly TableColumnWidth[]
  'data-testid'?: string
}) => (
  <colgroup data-testid={testId}>
    {columns.map((column) => (
      <TableCol key={column.id} $width={column.width} />
    ))}
  </colgroup>
)

// Table Container with styling — supports custom style and className props
interface TableContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

const TableContainer = styled.div<TableContainerProps>`
  background: var(--bg-card);
  border-radius: var(--radius-lg, 16px);
  border: var(--border-thin, 1px) solid var(--border-subtle);
  padding: var(--space-5);
  box-shadow: var(--shadow-strong);
`

export {
  Table,
  TableColGroup,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  MobileTableWrapper,
  TableContainer,
}

export type {TableContainerProps}
